import QueryString from 'query-string';

/* ==========================================================================
   What an address means, and where a view lives.

   One module because the two questions are the same question read in opposite
   directions, and a site whose links and address bar disagree is a site with
   two answers to it. Every href on the site and the history push both come from
   locationForRoute, so they cannot drift.

   Lifted out of App.js once the views began needing it: importing a helper from
   the component that imports the views is a cycle, and it worked only because
   the bundler is forgiving about them.
   ========================================================================== */

/* The Quick Look was called the Operations Scorecard until August 2026, and
   ?route=scorecard may be linked from outside this site. The old value is still
   accepted and resolves to the new view; nothing here ever emits it. Safe to
   drop once those links have aged out. */
const ROUTE_ALIASES = { scorecard: 'quick-look' };

/* Views addressed by a path of their own. The value is both the first segment
   of the url and the view name, because there is no reason for them to differ
   and every reason for one list to govern both. The legacy views are absent
   deliberately: they have only ever been reachable by ?route= and giving them
   paths would change behaviour nobody asked for. */
const PATH_VIEWS = ['articles', 'contact', 'deep-look', 'deep-look-report', 'how-we-work', 'privacy', 'quick-look', 'terms', 'ways-to-work'];

/* The Deep Look sells on one page and has one thing to say afterwards: /ready,
   which is the same page opening on connect instructions for somebody who has
   just paid. Not a page of its own, because somebody who lands there with
   nothing to confirm should simply be reading the page.

   /deep-look/signed-in is deliberately absent and must stay absent. That
   address belongs to the server: Apache maps it to api/v1/deep_look/signed-in,
   which is where AuthKit returns a browser carrying its code. Claiming it here
   would put the React app in front of the handler that completes a sign in.
   What comes back to this page from that handler is ?signin=ok, failed or
   cancelled, read below with the rest of the query string.

   Anything else after /deep-look resolves to the page itself. Stripe puts us
   back here through urls we hand it, so an unrecognised segment is a typo or a
   stale link, and the page answers either one better than a not-found. */
const DEEP_LOOK_STAGES = ['ready'];

export const routeFromLocation = () => {

    /* The path first, because that is the address the site now publishes and
       the one a reader can copy, share or type. */
    const segments = window.location.pathname.split('/').filter(Boolean);

    if(segments[0] === 'articles') {
        // an article carries its slug, so a link someone shares opens on the
        // piece they were reading rather than dropping them on the index
        return segments[1]
            ? { view: 'article', slug: decodeURIComponent(segments[1]) }
            : { view: 'articles' };
    }
    /* A shared Quick Look read carries the uuid that names it, the same way an
       article carries its slug. It is the address in the receipt email and the
       one an owner forwards to whoever else has a say, so it has to survive
       being pasted rather than only being clicked. */
    if(segments[0] === 'quick-look' && segments[1]) {
        return { view: 'quick-look', read: decodeURIComponent(segments[1]) };
    }

    /* The stage and the sign in result travel in the route so the view never
       reads the address bar itself. `session` is the checkout id Stripe appends
       to the success url. `signin` is what the AuthKit handler sends back, and
       it arrives on the bare page rather than on a stage of its own. */
    if(segments[0] === 'deep-look') {
        const parameters = QueryString.parse(window.location.search);
        const stage = DEEP_LOOK_STAGES.includes(segments[1]) ? segments[1] : null;
        return {
            view: 'deep-look',
            ...(stage ? { stage: stage } : {}),
            ...(parameters.session ? { session: parameters.session } : {}),
            ...(parameters.signin ? { signin: parameters.signin } : {})
        };
    }

    if(segments.length === 1 && PATH_VIEWS.includes(segments[0])) {
        return { view: segments[0] };
    }

    /* Then the query string, which is how every address worked until now.

       Kept for as long as those links exist anywhere: in a sent email, in a
       browser history, in somebody's notes. Reading them costs nothing and the
       alternative is breaking addresses that used to work. Nothing emits this
       form any more, and an arrival through it is rewritten to the path in
       App's history effect so the two never both sit in the address bar. */
    const parameters = QueryString.parse(window.location.search);
    const route = ROUTE_ALIASES[parameters.route] || parameters.route;
    switch(route) {

        case 'article':
        return { view: 'article', slug: parameters.slug };

        case 'articles':
        case 'contact':
        case 'how-we-work':
        case 'quick-look':
        case 'ways-to-work':
        case 'duracode':
        case 'duralink':
        case 'duralink_privacy':
        return { view: route };

        default:
        return { view: 'home' };
    }
}

/* The address a view should be at. One function, used for the history push and
   for every href on the site, so a link and the bar above it can never disagree
   about where a page lives. */
export const locationForRoute = active => {

    if(!active || active.view === 'home') {
        return '/';
    }
    if(active.view === 'article') {
        return active.slug ? `/articles/${encodeURIComponent(active.slug)}` : '/articles';
    }
    if(active.view === 'quick-look' && active.read) {
        return `/quick-look/${encodeURIComponent(active.read)}`;
    }
    /* The session id is deliberately not written back out. It is Stripe's
       handle on a checkout, it means nothing once the receipt has been shown,
       and leaving it in the bar invites somebody to share a url carrying it. */
    if(active.view === 'deep-look' && active.stage) {
        return `/deep-look/${active.stage}`;
    }
    if(PATH_VIEWS.includes(active.view)) {
        return `/${active.view}`;
    }

    // the legacy views, which have no path of their own
    return `/?route=${active.view}`;
}
